import React, { ReactElement } from 'react';
import ErrorsWrapper from '../uibuilder/form/input/layout/ErrorsWrapper';
import styles from '../../plans/tasks/CreateUpdate/TransactionParties.module.scss';
import RelativeRow, { Cell, EmptyCell } from '../web/uitheme/form/RelativeRow';
import { EmailRecipientType } from 'nekst-api';
import { BriefcaseCheckbox } from 'features/nekst-widgets';
import { CheckboxInput, LoadingAnimation } from 'ui-builder';

export type Choice = {
  id: string
  name: string | ReactElement
  title?: string
  isAssignable: boolean
  isAssigned: boolean
  emailRecipientType?: string | null
  hasPrimaryEmail?: boolean
}

export interface EmailTransactionPartiesInputLayoutProps {
  choices: Choice[]
  isDisabled: boolean
  numberOfSelectedOptions: number,
  onAssignmentChangedCallback: (id: string) => void,
  onEmailRecipientChangedCallback: (id: string, type?: EmailRecipientType) => void,
}

const NO_EMAIL_WARNING = 'This contact has no primary email set and cannot be set as a recipient';

function NoEmailWarningIcon() {
  return (
    <span className={styles.warning} title={NO_EMAIL_WARNING}>&nbsp;</span>
  );
}

export default function EmailTransactionPartiesInputLayout(
  props: EmailTransactionPartiesInputLayoutProps,
) {
  const title = `Transaction Parties (${props.numberOfSelectedOptions})`;
  return (
    <ErrorsWrapper name="emailRecipients">
      <ErrorsWrapper name="transactionParties">
        <div className={styles.block}>
          <h3>
            {title}
          </h3>
          {!!props.choices.length && (
            <div className={styles.table}>
              <RelativeRow
                weights={[4, 1, 3, 2, 2]}
                rowClassName={styles.tableHead}
                cellClassName={styles.cell}
              >
                <Cell>
                  Assigned To:
                </Cell>
                <EmptyCell />
                <Cell centered>
                  Receive Email
                </Cell>
                <Cell centered>
                  CC
                </Cell>
                <Cell centered>
                  BCC
                </Cell>
              </RelativeRow>
              <div className={styles.tableBody}>
                {props.choices.map(
                  (choice) => {
                    return (
                      <RelativeRow
                        key={`choice-${choice.id}`}
                        weights={[4, 1, 3, 2, 2]}
                        rowClassName={styles.row}
                        cellClassName={styles.cell}
                      >
                        {choice.isAssignable ? (
                          <BriefcaseCheckbox
                            title={choice.title}
                            source={`assigned.${choice.id}`}
                            label={choice.hasPrimaryEmail === false
                              ? <>{choice.name} <NoEmailWarningIcon /></>
                              : choice.name}
                            value={choice.isAssigned}
                            isDisabled={props.isDisabled}
                            onChangeCallback={() => {
                              props.onAssignmentChangedCallback(choice.id);
                            }}
                          />
                        ) : (
                          <Cell className={styles.noAssignment}>
                            <span title={choice.title}>
                              {choice.name}
                              {choice.hasPrimaryEmail === false && <NoEmailWarningIcon />}
                            </span>
                          </Cell>
                        )}
                        <EmptyCell />
                        <Cell centered className={styles.checkboxCell}>
                          <CheckboxInput
                            source={`receiveEmail.${choice.id}`}
                            label=""
                            value={choice.emailRecipientType === EmailRecipientType.RECIPIENT}
                            isDisabled={choice.hasPrimaryEmail === false && choice.emailRecipientType !== EmailRecipientType.RECIPIENT}
                            title={choice.hasPrimaryEmail === false ? NO_EMAIL_WARNING : undefined}
                            onChangeCallback={(data) => {
                              const selected = data[`receiveEmail.${choice.id}`];
                              if (selected) {
                                props.onEmailRecipientChangedCallback(
                                  choice.id,
                                  EmailRecipientType.RECIPIENT,
                                );
                              } else {
                                props.onEmailRecipientChangedCallback(
                                  choice.id,
                                  undefined,
                                );
                              }
                            }}
                          />
                        </Cell>
                        <Cell centered className={styles.checkboxCell}>
                          <CheckboxInput
                            source={`cc.${choice.id}`}
                            label=""
                            value={choice.emailRecipientType === EmailRecipientType.CC}
                            isDisabled={choice.hasPrimaryEmail === false && choice.emailRecipientType !== EmailRecipientType.CC}
                            title={choice.hasPrimaryEmail === false ? NO_EMAIL_WARNING : undefined}
                            onChangeCallback={(data) => {
                              const selected = data[`cc.${choice.id}`];
                              if (selected) {
                                props.onEmailRecipientChangedCallback(
                                  choice.id,
                                  EmailRecipientType.CC,
                                );
                              } else {
                                props.onEmailRecipientChangedCallback(
                                  choice.id,
                                  undefined,
                                );
                              }
                            }}
                          />
                        </Cell>
                        <Cell centered className={styles.checkboxCell}>
                          <CheckboxInput
                            source={`bcc.${choice.id}`}
                            label=""
                            value={choice.emailRecipientType === EmailRecipientType.BCC}
                            isDisabled={choice.hasPrimaryEmail === false && choice.emailRecipientType !== EmailRecipientType.BCC}
                            title={choice.hasPrimaryEmail === false ? NO_EMAIL_WARNING : undefined}
                            onChangeCallback={(data) => {
                              const selected = data[`bcc.${choice.id}`];
                              if (selected) {
                                props.onEmailRecipientChangedCallback(
                                  choice.id,
                                  EmailRecipientType.BCC,
                                );
                              } else {
                                props.onEmailRecipientChangedCallback(
                                  choice.id,
                                  undefined,
                                );
                              }
                            }}
                          />
                        </Cell>
                      </RelativeRow>
                    );
                  },
                )}
              </div>
            </div>
          )}
          {!props.choices.length && (<LoadingAnimation />)}
        </div>
      </ErrorsWrapper>
    </ErrorsWrapper>
  );
}
